import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setSubscribing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, interests: ['news', 'events', 'donations'] })
      });

      if (response.ok) {
        toast.success('Successfully subscribed to our newsletter!');
        setEmail('');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
    setSubscribing(false);
  };

  return (
    <footer className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h3 className="text-xl font-bold mb-4">Lions Unleashed</h3>
            <p className="text-sm opacity-90">
              Empowering minorities and young entrepreneurs through mentorship, resources, and networking opportunities.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/mission" className="hover:underline opacity-90 hover:opacity-100">Purpose & Mission</Link></li>
              <li><Link to="/team" className="hover:underline opacity-90 hover:opacity-100">Our Team</Link></li>
              <li><Link to="/donors" className="hover:underline opacity-90 hover:opacity-100">Our Donors</Link></li>
            </ul>
          </div>

          {/* Get Involved */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Get Involved</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/register/cub" className="hover:underline opacity-90 hover:opacity-100">Become a Cub</Link></li>
              <li><Link to="/register/lion" className="hover:underline opacity-90 hover:opacity-100">Become a Lion</Link></li>
              <li><Link to="/donate" className="hover:underline opacity-90 hover:opacity-100">Donate</Link></li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Connect With Us</h4>
            <div className="flex space-x-4 mb-4">
              <a href="https://x.com/lionsunleashed_" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <Twitter size={20} />
              </a>
              <a href="https://facebook.com/lionsunleashed.org" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <Facebook size={20} />
              </a>
              <a href="https://instagram.com/lionsunleashed" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <Instagram size={20} />
              </a>
              <a href="https://www.linkedin.com/company/lions-unleashed" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <Linkedin size={20} />
              </a>
            </div>
            <a href="mailto:info@lionsunleashed.org" className="text-sm hover:underline opacity-90 hover:opacity-100 flex items-center">
              <Mail size={16} className="mr-2" />
              info@lionsunleashed.org
            </a>
          </div>
        </div>

        {/* Newsletter Subscribe */}
        <div className="border-t border-white/20 mt-8 pt-8">
          <div className="max-w-md mx-auto text-center mb-8">
            <h4 className="text-lg font-semibold mb-2">Subscribe to Our Newsletter</h4>
            <p className="text-sm opacity-90 mb-4">Stay updated with our latest news, events, and success stories.</p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <button
                type="submit"
                disabled={subscribing}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Send size={16} />
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>

        {/* WFO Member Badge */}
        <div className="border-t border-white/20 mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-6">
            <a 
              href="http://worldfamilyorganization.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex items-center gap-4 hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_pixel-perfect-132/artifacts/7pa7brov_Logo-WFO-Vertical-White2.png" 
                alt="World Family Organization Member"
                className="h-20 md:h-24 object-contain"
              />
              <div className="text-left">
                <p className="text-xs uppercase tracking-wide opacity-75">Proud Member of</p>
                <p className="text-sm font-semibold">World Family Organization</p>
              </div>
            </a>
          </div>
        </div>

        <div className="border-t border-white/20 pt-6 text-center text-sm opacity-90">
          <p>&copy; {new Date().getFullYear()} Lions Unleashed. All rights reserved. | 501(c)(3) Nonprofit Organization</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;